import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * Modal for creating OR editing a Sale.
 * When `sale` prop is provided → EDIT mode (PATCH /api/sales/{id}).
 * Otherwise → CREATE mode (POST /api/sales/).
 * The parent passes `onSaved` which refreshes dashboard + sales list.
 */
export default function SaleModal({ isOpen, onClose, onSaved, sale = null }) {
  const [creatives, setCreatives] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [value, setValue] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [creativeId, setCreativeId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    (async () => {
      try {
        const [c, k] = await Promise.all([
          api.get("/api/creatives/"),
          api.get("/api/campaigns/"),
        ]);
        setCreatives(c.data?.creatives || []);
        setCampaigns(k.data?.campaigns || []);
      } catch (_e) { /* ignore */ }
    })();
    if (sale) {
      setValue(String(sale.value ?? ""));
      setQuantity(String(sale.quantity ?? 1));
      setCreativeId(sale.creative_id || "");
      setCampaignId(sale.campaign_id || "");
      setOccurredAt(sale.occurred_at ? sale.occurred_at.slice(0, 16) : "");
      setNotes(sale.notes || "");
    } else {
      setValue(""); setQuantity("1"); setCreativeId("");
      setCampaignId(""); setOccurredAt(""); setNotes("");
    }
  }, [isOpen, sale]);

  if (!isOpen) return null;

  const submit = async () => {
    setError("");
    const num = parseFloat(String(value).replace(",", "."));
    if (!num || num <= 0) { setError("Valor inválido."); return; }
    if (!creativeId) { setError("Selecione o criativo."); return; }
    setLoading(true);
    try {
      const body = {
        value: num,
        quantity: parseInt(quantity || "1", 10) || 1,
        creative_id: creativeId,
        notes: notes || undefined,
      };
      if (campaignId) body.campaign_id = campaignId;
      if (occurredAt) body.occurred_at = new Date(occurredAt).toISOString();
      if (sale?.id) {
        await api.patch(`/api/sales/${sale.id}`, body);
      } else {
        await api.post("/api/sales/", body);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Falha ao salvar venda.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="sale-modal-overlay"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid={sale ? "edit-sale-modal" : "create-sale-modal"}
        className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="close-sale-modal-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-extrabold text-white mb-4">
          {sale ? "Editar venda" : "Registrar venda"}
        </h2>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">Valor (R$) *</label>
            <input
              data-testid="sale-value-input"
              type="number" step="0.01" value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">Quantidade</label>
            <input
              data-testid="sale-quantity-input"
              type="number" min="1" value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">Criativo *</label>
            <select
              data-testid="sale-creative-select"
              value={creativeId}
              onChange={(e) => setCreativeId(e.target.value)}
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            >
              <option value="">— selecione —</option>
              {creatives.map((c) => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">
              Campanha <span className="text-gray-500">(opcional)</span>
            </label>
            <select
              data-testid="sale-campaign-select"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            >
              <option value="">— automático —</option>
              {campaigns.map((c) => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">Data/hora</label>
            <input
              data-testid="sale-occurred-at-input"
              type="datetime-local" value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">
              Notas <span className="text-gray-500">(opcional)</span>
            </label>
            <input
              data-testid="sale-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            />
          </div>
          {error && <div className="text-xs text-red-400">{error}</div>}
        </div>
        <button
          data-testid="submit-sale-btn"
          onClick={submit}
          disabled={loading}
          className="mt-5 w-full py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase flex items-center justify-center gap-1 disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (sale ? "Salvar alterações" : "Registrar venda")}
        </button>
      </div>
    </div>
  );
}
